<script setup lang="ts">
/**
 * An invitation to an appointment as a card: what, when, where, who invites,
 * who else is asked, and the reader's own answer. Presentational — the mail
 * reading pane and the calendar's invitation list both hand it their data and
 * react to `respond`.
 *
 * `when` is shown as given; without it the card formats `start`/`end` itself
 * (`formatEventRange`, the host's locale). The accept / tentative / decline row
 * appears with `respondable`; the button of the current answer is pressed, and
 * `pendingResponse` spins the one on its way. The `actions` slot adds further
 * buttons to that row (import into a calendar, open), `meta` puts badges next
 * to the heading, the default slot sits under the card body (banners).
 */
import { CheckmarkIcon, DismissIcon, QuestionCircleIcon } from '@blpde/nx-icons';
import { computed, inject } from 'vue';
import { NxBadge, type NxBadgeVariant } from '../Badge';
import { NxButton } from '../Button';
import { NxKeyValueList, type NxKeyValueItem } from '../KeyValueList';
import { i18nInjectionKey } from '../internal';
import { formatEventRange } from './eventFormat';
import type { NxCalendarEventResponse } from './types';

/** An answer the reader can give; `needs-action` is the absence of one. */
export type NxEventInvitationAnswer = Exclude<NxCalendarEventResponse, 'needs-action'>;

export type NxEventInvitationAttendeeResponse = NxCalendarEventResponse | 'delegated';

export interface NxEventInvitationAttendee {
  key?: string | number;
  name: string;
  response?: NxEventInvitationAttendeeResponse;
}

const props = withDefaults(
  defineProps<{
    title: string;
    /** Eyebrow above the title: „Termineinladung“, „Termin abgesagt“. */
    heading?: string;
    /** Accent of the card: an invitation, an answer (`info`), a cancellation (`danger`). */
    tone?: 'default' | 'info' | 'danger';
    /** Pre-formatted time span; wins over `start`/`end`. */
    when?: string;
    start?: Date | null;
    /** Exclusive end, as everywhere in the event views. */
    end?: Date | null;
    allDay?: boolean;
    location?: string;
    organizer?: string;
    attendees?: NxEventInvitationAttendee[];
    description?: string;
    /** The reader's own answer, shown as a badge and as the pressed button. */
    response?: NxCalendarEventResponse | null;
    /** Shows the accept / tentative / decline buttons. */
    respondable?: boolean;
    /** The answer being sent: its button spins, the others wait. */
    pendingResponse?: NxEventInvitationAnswer | null;
    locale?: string;
    untitledLabel?: string;
    unknownTimeLabel?: string;
    allDayLabel?: string;
    whenLabel?: string;
    locationLabel?: string;
    organizerLabel?: string;
    attendeesLabel?: string;
    respondLabel?: string;
    acceptLabel?: string;
    tentativeLabel?: string;
    declineLabel?: string;
    responseLabels?: Partial<Record<NxEventInvitationAttendeeResponse, string>>;
  }>(),
  {
    heading: undefined,
    tone: 'default',
    when: undefined,
    start: null,
    end: null,
    allDay: false,
    location: undefined,
    organizer: undefined,
    attendees: () => [],
    description: undefined,
    response: null,
    respondable: false,
    pendingResponse: null,
    locale: undefined,
    untitledLabel: '(Ohne Titel)',
    unknownTimeLabel: 'Zeit unbekannt',
    allDayLabel: 'ganztägig',
    whenLabel: 'Zeit',
    locationLabel: 'Ort',
    organizerLabel: 'Organisator',
    attendeesLabel: 'Teilnehmer',
    respondLabel: 'Antworten:',
    acceptLabel: 'Annehmen',
    tentativeLabel: 'Vorbehalt',
    declineLabel: 'Ablehnen',
    responseLabels: () => ({}),
  }
);

const emit = defineEmits<{ respond: [answer: NxEventInvitationAnswer] }>();

defineSlots<{
  /** Badges next to the heading („Live“, „Übernommen“). */
  meta?: () => unknown;
  /** Further buttons in the answer row. */
  actions?: () => unknown;
  /** Under the card body — status banners. */
  default?: () => unknown;
}>();

const DEFAULT_RESPONSE_LABELS: Record<NxEventInvitationAttendeeResponse, string> = {
  accepted: 'Zugesagt',
  tentative: 'Vorläufig',
  declined: 'Abgesagt',
  'needs-action': 'Offen',
  delegated: 'Delegiert',
};

const RESPONSE_VARIANT: Record<NxEventInvitationAttendeeResponse, NxBadgeVariant> = {
  accepted: 'success',
  tentative: 'warning',
  declined: 'danger',
  'needs-action': 'neutral',
  delegated: 'info',
};

const i18n = inject(i18nInjectionKey, null);
const resolvedLocale = computed(() => props.locale ?? i18n?.locale?.value ?? undefined);
const responseText = computed(() => ({ ...DEFAULT_RESPONSE_LABELS, ...props.responseLabels }));

const whenText = computed(() => {
  if (props.when) return props.when;
  if (!props.start) return props.unknownTimeLabel;
  return formatEventRange(props.start, props.end, {
    locale: resolvedLocale.value,
    allDay: props.allDay,
    allDayLabel: props.allDayLabel,
    fallback: props.unknownTimeLabel,
  });
});

const details = computed<NxKeyValueItem[]>(() => {
  const items: NxKeyValueItem[] = [{ key: 'when', label: props.whenLabel, value: whenText.value }];
  if (props.location)
    items.push({ key: 'location', label: props.locationLabel, value: props.location });
  if (props.organizer) {
    items.push({ key: 'organizer', label: props.organizerLabel, value: props.organizer });
  }
  if (props.attendees.length) {
    items.push({
      key: 'attendees',
      label: props.attendeesLabel,
      value: String(props.attendees.length),
    });
  }
  return items;
});

const answers = computed(() => [
  { value: 'accepted' as const, label: props.acceptLabel, icon: CheckmarkIcon },
  { value: 'tentative' as const, label: props.tentativeLabel, icon: QuestionCircleIcon },
  { value: 'declined' as const, label: props.declineLabel, icon: DismissIcon },
]);

function attendeeIcon(response: NxEventInvitationAttendeeResponse | undefined) {
  if (response === 'accepted') return CheckmarkIcon;
  if (response === 'declined') return DismissIcon;
  if (response === 'tentative') return QuestionCircleIcon;
  return null;
}
</script>

<template>
  <article :class="['nx-event-invitation', `tone-${tone}`]">
    <header class="nx-event-invitation__head">
      <div class="nx-event-invitation__titles">
        <span v-if="heading" class="nx-event-invitation__heading">{{ heading }}</span>
        <h3 class="nx-event-invitation__title">{{ title || untitledLabel }}</h3>
      </div>
      <div v-if="response || $slots.meta" class="nx-event-invitation__meta">
        <NxBadge v-if="response" :variant="RESPONSE_VARIANT[response]">
          {{ responseText[response] }}
        </NxBadge>
        <slot name="meta" />
      </div>
    </header>

    <NxKeyValueList dense :items="details" layout="inline">
      <template #value="{ item }">
        <ul v-if="item.key === 'attendees'" class="nx-event-invitation__attendees">
          <li
            v-for="attendee in attendees"
            :key="attendee.key ?? attendee.name"
            :class="`response-${attendee.response ?? 'needs-action'}`"
            :title="responseText[attendee.response ?? 'needs-action']"
          >
            <component
              :is="attendeeIcon(attendee.response)"
              v-if="attendeeIcon(attendee.response)"
              aria-hidden="true"
              class="nxt-icon-xs"
            />
            <span>{{ attendee.name }}</span>
            <span class="nx-event-invitation__sr">
              {{ responseText[attendee.response ?? 'needs-action'] }}
            </span>
          </li>
        </ul>
        <span v-else class="nx-event-invitation__value">{{ item.value }}</span>
      </template>
    </NxKeyValueList>

    <p v-if="description" class="nx-event-invitation__description">{{ description }}</p>

    <div v-if="respondable || $slots.actions" class="nx-event-invitation__actions">
      <template v-if="respondable">
        <span class="nx-event-invitation__respond-label">{{ respondLabel }}</span>
        <NxButton
          v-for="answer in answers"
          :key="answer.value"
          :aria-pressed="response === answer.value"
          :data-response="answer.value"
          :disabled="
            response === answer.value ||
            (pendingResponse !== null && pendingResponse !== answer.value)
          "
          height="xs"
          :loading="pendingResponse === answer.value"
          :variant="response === answer.value ? 'default' : 'outline'"
          @click="emit('respond', answer.value)"
        >
          <component :is="answer.icon" aria-hidden="true" class="nxt-icon-sm" />
          {{ answer.label }}
        </NxButton>
      </template>
      <slot name="actions" />
    </div>

    <slot />
  </article>
</template>

<style scoped>
.nx-event-invitation {
  display: grid;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-md);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-inline-start: calc(var(--nxt-hairline) * 4) solid var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
}

.nx-event-invitation.tone-info {
  border-inline-start-color: var(--nxt-info-border);
}

.nx-event-invitation.tone-danger {
  border-inline-start-color: var(--nxt-danger-border);
}

.nx-event-invitation__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
}

.nx-event-invitation__titles {
  display: grid;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

.nx-event-invitation__heading {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.nx-event-invitation__title {
  margin: 0;
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  overflow-wrap: anywhere;
}

.nx-event-invitation__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
}

.nx-event-invitation__value {
  overflow-wrap: anywhere;
}

.nx-event-invitation__attendees {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}

.nx-event-invitation__attendees li {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: 0 var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
}

.nx-event-invitation__attendees li.response-accepted svg {
  color: var(--nxt-success-bg);
}

.nx-event-invitation__attendees li.response-declined {
  color: var(--nxt-fg-muted);
  text-decoration: line-through;
}

.nx-event-invitation__attendees li.response-declined svg {
  color: var(--nxt-danger-bg);
}

.nx-event-invitation__attendees li.response-tentative svg {
  color: var(--nxt-warning-bg);
}

.nx-event-invitation__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.nx-event-invitation__description {
  margin: 0;
  color: var(--nxt-fg-muted);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.nx-event-invitation__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding-block-start: var(--nxt-space-sm);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-event-invitation__respond-label {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
</style>
